"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Ported from the "view on mobile" block of profile/show.blade.php — a
 * desktop-only floating chip that pops a QR of this profile's URL. Hidden
 * below sm: (you're already on mobile). The `?preview` guard from Blade isn't
 * needed yet — the rewrite has no editor preview iframe.
 */
export function QrChip({ svg, displayUrl }: { svg: string; displayUrl: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      data-marketing-hide
      className="fixed right-4 bottom-4 z-40 hidden sm:block"
    >
      {open ? (
        <div className="absolute right-0 bottom-full mb-2 w-60 border border-(--paw-line-strong) bg-(--paw-ink) p-4 text-center">
          <p className="font-paw-mono text-[10px] font-bold tracking-[0.14em] text-(--paw-steel-dim) uppercase">
            Scan to open on your phone
          </p>
          <div
            className="mx-auto mt-3 w-40 bg-white p-2"
            dangerouslySetInnerHTML={{ __html: svg }}
          />
          <p className="mt-3 font-mono text-[10px] break-all text-(--paw-steel-faint)">
            {displayUrl}
          </p>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-2 border border-(--paw-line-strong) bg-(--paw-ink) px-3 py-2 font-paw-mono text-[10px] font-bold tracking-[0.14em] text-(--paw-steel) uppercase transition hover:border-(--paw-signal) hover:text-(--paw-paper)"
      >
        <i className="fa-solid fa-qrcode" aria-hidden="true" />
        View on mobile
      </button>
    </div>
  );
}
